import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import _ from 'lodash';
import Translate from "utils/translate";
import { dataConstants } from "services/redux/data/constants";
import ObjectTimeslot from "utils/objects/timeslot";
import { cartConstants } from "services/redux/cart/constants";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import ThemedCalendarStrip from "components/atoms/ThemedCalendarStrip";
import { getNavigateToFromSelect, RenderBookingCreateItem, renderEmptyBookingCreateItem } from "components/atoms/BookingCreate";
import HelperString from 'utils/helper-string'
import { SET_CART } from 'services/redux/action-types'
import HelperLocaleSettings from 'utils/helper/HelperLocaleSettings'
import { localeSettingConstants } from 'utils/constants/LocaleSettings'
import { AppRoutes } from 'navigations/app-routes'
import CapacityFetcher from 'scenes/home/book/timeslots/_components/CapacityFetcher'
import LoadingComponent from 'components/atoms/Loading'
import { useStateWithCallbackLazy } from 'use-state-with-callback'
import { routeBookingConstants } from 'utils/constants/route/RouteBookingConstants'
import HelperTimeslotGroups from 'utils/helper/HelperTimeslotGroups'
import selectTimeslotGroup from 'services/redux/data/selectors/selectTimeslotGroup'
import useRouteParameters from 'utils/hooks/use-route-parameters'
import FooterBar from 'components/organisms/FooterBar'
import { FooterStyles } from 'styles/components/organisms/Footer'
import i18n from 'utils/i18n'
import FormSubmitButton from 'components/atoms/FormSubmitButton'
import useRouteParametersForcedGuids from 'utils/hooks/use-route-parameters-forced-guids'
import { layoutStyles } from 'styles/layout'
import { isBookForMyself } from 'utils/constants/booking-constants'
import HelperDate from 'utils/helper-date'

const HomeBookTimeslotPreviewScene = () => {
	
	const route = useRoute();
	const dispatch = useDispatch()
	const navigation = useNavigation();
	const _routeParameters = useRouteParameters( route );
	const _forcedGuids = useRouteParametersForcedGuids( route );
	const layoutRef = useRef()
	
	const _timeslots = useSelector( state => {
		return state.cart[cartConstants.TIMESLOTS];
	} ); // do not remove, required for reloading
	const _timeslotGroup = useSelector( state => {
		return selectTimeslotGroup(state, _.get(_routeParameters, routeBookingConstants.TIMESLOT_GROUP));
	} ); // do not remove, required for reloading
	const _timeslotKinds = useSelector( state => {
		return state.data[dataConstants.TIMESLOT_KINDS];
	} ); // do not remove, required for reloading
	
	const { timeslotKinds, timeslotSubKinds } = HelperTimeslotGroups.getKindsAndSubKinds( _timeslotGroup, _timeslotKinds );
	
	const _bookDate = useSelector( state => {
		return state.cart[cartConstants.BOOK_DATE];
	} );
	
	const selectDateCallback = useRef();
	const calendarStripRef = useRef();
	
	const [ loading, setLoading ] = useState( true )
	const [ footerHeight, setFooterHeight ] = useState( 0 )
	const [ timeslots, setTimeslots ] = useStateWithCallbackLazy( {} )
	
	const _buildings = useSelector( state => {
		return state.data[dataConstants.BUILDINGS];
	} )
	const _floors = useSelector( state => {
		return state.data[dataConstants.FLOORS];
	} )
	const _rooms = useSelector( state => {
		return state.data[dataConstants.ROOMS];
	} )
	
	const _bookForOption = _.get( _routeParameters, routeBookingConstants.FOR_OPTION );
	
	const building = _.find( _buildings, { guid: _.get(  _routeParameters, routeBookingConstants.BUILDING, null ) } );
	const floor = _.find( _floors, { guid: _.get( _routeParameters, routeBookingConstants.FLOOR, null ) } );
	const room = _.find( _rooms, { guid: _.get( _routeParameters, routeBookingConstants.ROOM, null ) } );
	
	const { navigateTo } = getNavigateToFromSelect( AppRoutes.ADD_BOOKING_DATE, isBookForMyself( _bookForOption ) );
	
	const onModeChange = ( mode ) => {
		HelperLocaleSettings.set( localeSettingConstants.CALENDAR_VIEW_MODE_BOOKINGS, mode );
	}
	
	const renderEmptyItem = ( item ) => {
		return renderEmptyBookingCreateItem( loading );
	}
	
	const selectDate = ( date = HelperDate.date(), callback = null ) => {
		if( _.isFunction( callback ) ) {
			selectDateCallback.current = callback
		}
		
		dispatch({
			type: SET_CART,
			key: cartConstants.BOOK_DATE,
			payload: date
		})
	}
	
	const saveBooking = () => {
		navigation.navigate( navigateTo, {
			..._routeParameters,
			building: _.get( building, 'guid', null ),
			floor: _.get( floor, 'guid', null ),
			room: _.get( room, 'guid', null )
		});
	}
	
	const createTimeslots = ( date ) => {
		// TODO fix the roomKind issue
		setTimeslots( ObjectTimeslot.find({ building, floor, room, timeslotKinds: timeslotKinds }), ( data ) => {
			setLoading(false )
		} )
	}
	
	const onCapacityFetchCallback = ( day, createdTimeslot ) => {
		if( _.isFunction( selectDateCallback.current ) ) {
			selectDateCallback.current({
				date: day.bookingDate,
				items: createdTimeslot
			})
		}
		
		if( loading !== false ) {
			setLoading( false )
		}
	}
	
	const createTitles = () => {
		let title = Translate.field( _timeslotGroup, 'title', 'name' );
		let subtitle = '';
		
		const subtitles = [];
		
		if( !_.isEmpty( building ) ) {
			subtitles.push( Translate.field( building, 'title' ) );
		}
		
		if( !_.isEmpty( floor ) ) {
			subtitles.push( Translate.field( floor, 'title' ) );
		}
		
		if( !_.isEmpty( room ) ) {
			subtitles.push( Translate.field( room, 'title' ) );
		}
		
		subtitle = `${ subtitles.join(", ") }`;
		
		return {
			title,
			subtitle
		}
	}
	
	useEffect( createTimeslots.bind( this, _bookDate ), [] )
	
	useEffect( createTimeslots.bind( this, _bookDate ), [ _buildings, _floors, _rooms, building, floor, room, _bookDate ]);
	
	const { title, subtitle } = createTitles()
	
	const loadingContent = loading ? <LoadingComponent containerStyle={ layoutStyles.flexNone } /> : null
	
	const gotoBooking = () => {
		let params = {..._routeParameters };
		delete params[ routeBookingConstants.BUILDING ];
		delete params[ routeBookingConstants.FLOOR ];
		delete params[ routeBookingConstants.ROOM ];
		
		navigation.navigate( AppRoutes.TAB_BOOK, params );
	}
	
	const filterCalendarDayItems = ( calendarDayItems ) => {
		if( _.isEmpty( _forcedGuids[ routeBookingConstants.FORCED_TIMESLOTS ] ) ) return calendarDayItems;
		
		return _.map( calendarDayItems, ( calendarDayItem ) => {
			let timeslotKind = _.get( _timeslotKinds,  `${ calendarDayItem.timeslot?.kind }_${ calendarDayItem.timeslot?.sub_kind }`, null );
			if( timeslotKind === null || !_.includes( _forcedGuids[ routeBookingConstants.FORCED_TIMESLOTS ],  timeslotKind.guid ) ) return;
			
			return calendarDayItem;
		});
	}
	
	const footer = useMemo( () => {
		if( loading ) {
			return null;
		}
		
		return (
			<FooterBar
				buttons={
					<FormSubmitButton
						onPress={ gotoBooking }
						style={ [ FooterStyles.button, FooterStyles.buttonFill ] }>
						
						{ i18n.t( 'buttons.book' ) }
					</FormSubmitButton>
				}
				onHeightCallback={ layoutRef.current?.setFooterHeight }
			/>
		)
	}, [ loading ])
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>

			<ThemedLayout
				contentWidth="narrow"
				ref={ layoutRef }
				topNavigation
				showBackButton={ true }
				title={ title }
				subtitle={ subtitle }
				style={ {
					paddingBottom: footerHeight + 10
				} }>
				
				<CapacityFetcher
					key={ HelperString.hash('capacity-fetcher') }
					building={ building }
					floor={ floor }
					room={ room }
					timeslots={ timeslots }
					timeslotKinds={ timeslotKinds }
					timeslotSubKinds={ timeslotSubKinds }
					callback={ onCapacityFetchCallback }
				/>
				
				<ThemedCalendarStrip
					key={ HelperString.hash( 'themed-calendar-strip', building ) }
					innerRef={ calendarStripRef }
					selectDate={ selectDate }
					enableMinDate={ false }
					date={ _bookDate }
					timeslotKinds={ timeslotKinds }
					filterCalendarDayItems={ filterCalendarDayItems }
					renderItem={ (item, options ) => {
						return RenderBookingCreateItem(item, {
							selectable: null,
							selectableDisabled: true,
							previewing: true
						});
					} }
					renderEmptyItem={ renderEmptyItem.bind( this ) }
					onModeChange={ onModeChange.bind( this ) }>
					
					{ loadingContent }
				</ThemedCalendarStrip>
			</ThemedLayout>
			
			{ footer }
		</ThemedSafeAreaComponentTop>
	)
}

export default HomeBookTimeslotPreviewScene

